import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ShareBar from "@/pages/detail/components/ShareBar";
import {
  getArticleById,
  getRelated,
  getLatest,
  formatDate,
  formatViews,
  sectionLabels,
  sectionPaths,
} from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id } = useParams();
  const article = id ? getArticleById(id) : undefined;

  useDocumentMeta({
    title: article ? `${article.title} - 汽车资讯网` : "内容未找到 - 汽车资讯网",
    description: article ? article.summary : "抱歉，你访问的内容不存在或已被移除。",
    keywords: article ? article.tags.join(",") : "汽车资讯",
  });

  if (!article) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-4xl text-accent-500" />
        </span>
        <h1 className="font-heading font-bold text-2xl text-foreground-950">
          内容未找到
        </h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-md">
          抱歉，你访问的资讯不存在或已被移除，可能链接已失效。你可以返回首页继续浏览其他内容。
        </p>
        <Link
          to="/"
          className="mt-6 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm flex items-center gap-1.5 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(article, 4);
  const sidebar = getLatest(6).filter((item) => item.id !== article.id).slice(0, 5);

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: sectionLabels[article.section], to: sectionPaths[article.section] },
          { label: article.category, to: `${sectionPaths[article.section]}/${article.category}` },
          { label: "正文" },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <article className="lg:col-span-2 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="p-5 md:p-8 pb-0">
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700">
                {sectionLabels[article.section]}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800">
                {article.category}
              </span>
              {article.brand && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-800">
                  {article.brand}
                </span>
              )}
            </div>

            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-4 flex items-center flex-wrap gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读约 {article.readTime} 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>
          </div>

          <div className="mt-5 h-56 md:h-80 bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title}封面图`}
              title={`${article.brand}${article.title}`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="p-5 md:p-8">
            <div className="border-l-4 border-accent-500 bg-background-100 rounded-r-md px-4 py-3 text-sm text-foreground-700 leading-relaxed">
              {article.summary}
            </div>

            <div className="mt-6 space-y-5 text-base text-foreground-800 leading-[1.9]">
              {article.content.map((para, i) => (
                <p key={i} className={i === 0 ? "first-letter:text-3xl first-letter:font-heading first-letter:font-bold first-letter:text-primary-700 first-letter:mr-1" : ""}>
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <ShareBar />
              <Link
                to={`${sectionPaths[article.section]}/${article.category}`}
                className="h-9 px-4 rounded-md bg-secondary-600 text-background-50 text-sm flex items-center gap-1.5 hover:bg-secondary-700 transition-colors cursor-pointer whitespace-nowrap w-fit"
              >
                <i className="ri-arrow-go-back-line" />
                返回{article.category}
                {sectionLabels[article.section]}
              </Link>
            </div>
          </div>
        </article>

        <aside className="flex flex-col gap-6">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-lg text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-fire-line text-accent-500" />
              编辑推荐
            </h3>
            <div className="flex flex-col gap-4">
              {sidebar.map((item) => (
                <ArticleCard key={item.id} article={item} variant="compact" />
              ))}
            </div>
          </div>

          <div className="bg-primary-950 rounded-lg p-5 text-background-200">
            <h3 className="font-heading font-semibold text-lg text-background-50 flex items-center gap-2 mb-4">
              <i className="ri-question-answer-line text-accent-400" />
              栏目导航
            </h3>
            <div className="flex flex-col gap-2">
              {(
                [
                  { to: "/news", label: "汽车新闻" },
                  { to: "/reviews", label: "车型评测" },
                  { to: "/industry", label: "行业动态" },
                ] as const
              ).map((nav) => (
                <Link
                  key={nav.to}
                  to={nav.to}
                  className="flex items-center justify-between px-4 py-2.5 rounded-md bg-primary-900 hover:bg-primary-800 transition-colors cursor-pointer text-sm"
                >
                  {nav.label}
                  <i className="ri-arrow-right-s-line" />
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-7 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-xl text-foreground-950">
              相关内容推荐
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} variant="grid" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}